import React, { useState, useEffect } from "react";
import { customFetch } from "../apiClient";
import Swal from "sweetalert2";

const initialForm = {
  id: null,
  packageName: "",
  amount: "",
  isActive: true,
  isMedicineBillingIncluded: false,
  isBillingIncluded: false,       // NEW
  isLaboratoryIncluded: false     // NEW
};

const PackageMaster = () => {
  const [form, setForm] = useState(initialForm);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [searchText, setSearchText] = useState("");

  const fetchPackages = async () => {
    setFetching(true);
    try {
      const res = await customFetch("/PackageMaster");
      if (res.ok) {
        const data = await res.json();
        setPackages(data || []);
      } else {
        throw new Error("Failed to load packages");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch packages from server.");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => { fetchPackages(); }, []);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(t); }
  }, [success]);

  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(""), 4000); return () => clearTimeout(t); }
  }, [error]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.packageName.trim()) return setError("Package Name is required.");
    if (form.amount === "" || form.amount < 0) return setError("Valid amount is required.");

    setLoading(true);
    setError("");
    setSuccess("");

    const payload = {
      id: form.id || 0,
      packageName: form.packageName.trim(),
      amount: Number(form.amount),
      isActive: form.isActive,
      isMedicineBillingIncluded: form.isMedicineBillingIncluded,
      isBillingIncluded: form.isBillingIncluded,           // UPDATED
      isLaboratoryIncluded: form.isLaboratoryIncluded      // UPDATED
    };

    try {
      let res;
      if (isEditing) {
        res = await customFetch(`/PackageMaster/${form.id}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
      } else {
        res = await customFetch("/PackageMaster", {
          method: "POST",
          body: JSON.stringify(payload)
        });
      }
      if (!res.ok) throw new Error(await res.text() || "Failed to save package");
      setSuccess(isEditing ? "Package updated successfully!" : "New package created successfully!");
      handleReset();
      fetchPackages();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pkg) => {
    setForm({
      id: pkg.id,
      packageName: pkg.packageName,
      amount: pkg.amount,
      isActive: pkg.isActive,
      isMedicineBillingIncluded: pkg.isMedicineBillingIncluded,
      isBillingIncluded: pkg.isBillingIncluded,             // UPDATED
      isLaboratoryIncluded: pkg.isLaboratoryIncluded        // UPDATED
    });
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, name) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete package: ${name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await customFetch(`/PackageMaster/${id}`, { method: "DELETE" });
          if (!res.ok) throw new Error("Failed to delete");
          Swal.fire("Deleted!", "Package has been removed.", "success");
          fetchPackages();
          if (form.id === id) handleReset();
        } catch (err) {
          Swal.fire("Error!", err.message || "Failed to delete package.", "error");
        }
      }
    });
  };

  const handleReset = () => {
    setForm(initialForm);
    setIsEditing(false);
    setError("");
  };

  const filteredPackages = packages.filter(p =>
    p.packageName.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="dc-wrap">
      <div className="container-fluid py-3" style={{ maxWidth: '1100px' }}>

        {/* ===== FORM SECTION ===== */}
        <div className="dc-card mb-4 shadow-sm">
          <div className="dc-header d-flex justify-content-between align-items-center">
            <div className="dc-header-brand">
              <div className="dc-header-icon"><i className="bi bi-box-seam"></i></div>
              <span className="dc-header-title">{isEditing ? "Update Package" : "Create New Package"}</span>
            </div>
            {isEditing && (
              <span className="badge bg-warning text-dark"><i className="bi bi-pencil-fill me-1"></i> Edit Mode</span>
            )}
          </div>

          <div className="dc-body">
            {error && <div className="alert-custom alert-danger slide-in"><i className="bi bi-exclamation-triangle-fill"></i> <span>{error}</span></div>}
            {success && <div className="alert-custom alert-success slide-in"><i className="bi bi-check-circle-fill"></i> <span>{success}</span></div>}

            <form onSubmit={handleSubmit}>
              <div className="dc-section" style={{ background: 'transparent', border: 'none', marginBottom: 0 }}>
                
                {/* ROW 1: Basic Details */}
                <div className="row g-3 align-items-end mb-3">
                  <div className="col-md-5">
                    <label className="dc-label">Package Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control-custom"
                      name="packageName"
                      value={form.packageName}
                      onChange={handleChange}
                      placeholder="e.g. Normal Delivery Package"
                      autoFocus
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="dc-label">Amount (₹) <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      className="form-control-custom"
                      name="amount"
                      value={form.amount}
                      onChange={handleChange}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="col-md-4 text-end pb-1">
                    <div className="form-check form-switch custom-switch d-inline-block mt-1">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isActiveSwitch"
                        name="isActive"
                        checked={form.isActive}
                        onChange={handleChange}
                      />
                      <label
                        className="form-check-label fw-bold ms-2"
                        htmlFor="isActiveSwitch"
                        style={{ fontSize: '12px', color: form.isActive ? '#15803d' : '#64748b' }}
                      >
                        {form.isActive ? 'Status: Active' : 'Status: Inactive'}
                      </label>
                    </div>
                  </div>
                </div>

                {/* ROW 2: Included Services & Actions */}
                <div className="row g-3 align-items-center bg-light p-2 rounded border mx-0 mt-2">
                  <div className="col-md-3">
                    <div className="form-check form-switch custom-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="medSwitch"
                        name="isMedicineBillingIncluded"
                        checked={form.isMedicineBillingIncluded}
                        onChange={handleChange}
                      />
                      <label className="form-check-label ms-1 fw-bold" htmlFor="medSwitch" style={{ fontSize: '11px', color: form.isMedicineBillingIncluded ? '#1d4ed8' : '#64748b' }}>
                        Medicine Incl.
                      </label>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="form-check form-switch custom-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="billSwitch"
                        name="isBillingIncluded"
                        checked={form.isBillingIncluded}
                        onChange={handleChange}
                      />
                      <label className="form-check-label ms-1 fw-bold" htmlFor="billSwitch" style={{ fontSize: '11px', color: form.isBillingIncluded ? '#1d4ed8' : '#64748b' }}>
                        Billing Incl.
                      </label>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="form-check form-switch custom-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="labSwitch"
                        name="isLaboratoryIncluded"
                        checked={form.isLaboratoryIncluded}
                        onChange={handleChange}
                      />
                      <label className="form-check-label ms-1 fw-bold" htmlFor="labSwitch" style={{ fontSize: '11px', color: form.isLaboratoryIncluded ? '#1d4ed8' : '#64748b' }}>
                        Laboratory Incl.
                      </label>
                    </div>
                  </div>

                  <div className="col-md-3 text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <button type="button" className="btn-reset" onClick={handleReset} disabled={loading}>
                        Cancel
                      </button>
                      <button type="submit" className="btn-submit" disabled={loading}>
                        {loading ? <span className="spinner-border spinner-border-sm"></span> : (isEditing ? "Update" : "Save")}
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </form>
          </div>
        </div>

        {/* ===== LIST SECTION ===== */}
        <div className="dc-card shadow-sm">
          <div className="section-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-list-task"></i>
              <span>Existing Packages ({filteredPackages.length})</span>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="search-input-wrapper" style={{ width: '200px' }}>
                <i className="bi bi-search search-icon"></i>
                <input
                  type="text"
                  className="form-control-custom search-input"
                  placeholder="Search package..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ height: '28px' }}
                />
              </div>
              <div className="view-toggle">
                <span className={viewMode === "table" ? "active" : ""} onClick={() => setViewMode("table")}>
                  <i className="bi bi-table"></i> Table
                </span>
                <span className={viewMode === "card" ? "active" : ""} onClick={() => setViewMode("card")}>
                  <i className="bi bi-grid-fill"></i> Card
                </span>
              </div>
            </div>
          </div>

          <div className="dc-body p-3 bg-light rounded-bottom">
            {fetching ? (
              <div className="text-center py-5 text-muted">
                <div className="spinner-border spinner-border-sm text-primary mb-2 me-2" role="status"></div>
                Loading packages...
              </div>
            ) : filteredPackages.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-inbox fs-2 d-block mb-2 opacity-50"></i>
                {searchText ? "No packages matched your search." : "No packages available. Create one above."}
              </div>
            ) : viewMode === "table" ? (

              /* ---- TABLE VIEW ---- */
              <div className="dc-table-wrapper border rounded bg-white">
                <table className="dc-table mb-0">
                  <thead>
                    <tr>
                      <th width="8%">ID</th>
                      <th width="28%">Package Name</th>
                      <th width="15%">Amount (₹)</th>
                      <th width="22%">Included Services</th>
                      <th width="13%" className="text-center">Status</th>
                      <th width="14%" className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPackages.map(pkg => (
                      <tr key={pkg.id} className={form.id === pkg.id ? "row-editing" : ""}>
                        <td className="fw-bold text-muted">#{pkg.id}</td>
                        <td className="fw-bold text-dark">{pkg.packageName}</td>
                        <td className="text-primary fw-bold">₹ {pkg.amount.toFixed(2)}</td>
                        <td>
                          {/* 3 Badges for Services */}
                          <div className="d-flex flex-wrap gap-1">
                            <span className={`badge-status-sm ${pkg.isMedicineBillingIncluded ? 'badge-medicine' : 'badge-medicine-no'}`} title="Medicine">
                              <i className={`bi ${pkg.isMedicineBillingIncluded ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>Med
                            </span>
                            <span className={`badge-status-sm ${pkg.isBillingIncluded ? 'badge-medicine' : 'badge-medicine-no'}`} title="Billing">
                              <i className={`bi ${pkg.isBillingIncluded ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>Bill
                            </span>
                            <span className={`badge-status-sm ${pkg.isLaboratoryIncluded ? 'badge-medicine' : 'badge-medicine-no'}`} title="Laboratory">
                              <i className={`bi ${pkg.isLaboratoryIncluded ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>Lab
                            </span>
                          </div>
                        </td>
                        <td className="text-center">
                          <span className={`badge-status ${pkg.isActive ? 'badge-active' : 'badge-inactive'}`}>
                            {pkg.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            <button className="btn-icon btn-edit" onClick={() => handleEdit(pkg)} title="Edit">
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button className="btn-icon btn-delete" onClick={() => handleDelete(pkg.id, pkg.packageName)} title="Delete">
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            ) : (

              /* ---- CARD VIEW ---- */
              <div className="cards-container">
                {filteredPackages.map(pkg => (
                  <div key={pkg.id} className={`theme-glass-card ${form.id === pkg.id ? "card-editing" : ""}`}>
                    <div className="glass-header">
                      <span className="glass-title text-truncate" title={pkg.packageName}>
                        <i className="bi bi-box me-1"></i> {pkg.packageName}
                      </span>
                      <span className={`badge-status-sm ${pkg.isActive ? 'badge-active' : 'badge-inactive'}`}>
                        {pkg.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="glass-body">
                      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <span className="text-muted small fw-bold">ID: #{pkg.id}</span>
                        <span className="fs-5 fw-bold text-primary">₹ {pkg.amount.toFixed(2)}</span>
                      </div>

                      {/* Inclusion Details in Card */}
                      <div className="mb-3">
                        <div className="text-muted small fw-bold mb-1">Included Services:</div>
                        <div className="d-flex flex-wrap gap-1">
                          <span className={`badge-status-sm ${pkg.isMedicineBillingIncluded ? 'badge-medicine' : 'badge-medicine-no'}`}>
                            <i className={`bi ${pkg.isMedicineBillingIncluded ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>Med
                          </span>
                          <span className={`badge-status-sm ${pkg.isBillingIncluded ? 'badge-medicine' : 'badge-medicine-no'}`}>
                            <i className={`bi ${pkg.isBillingIncluded ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>Bill
                          </span>
                          <span className={`badge-status-sm ${pkg.isLaboratoryIncluded ? 'badge-medicine' : 'badge-medicine-no'}`}>
                            <i className={`bi ${pkg.isLaboratoryIncluded ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>Lab
                          </span>
                        </div>
                      </div>

                      <div className="glass-footer-btns">
                        <button className="g-btn g-btn-edit w-100 me-2" onClick={() => handleEdit(pkg)}>
                          <i className="bi bi-pencil-square"></i> Edit
                        </button>
                        <button className="g-btn g-btn-del w-100" onClick={() => handleDelete(pkg.id, pkg.packageName)}>
                          <i className="bi bi-trash"></i> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>

      <style jsx>{`
        .dc-wrap { font-family: system-ui, sans-serif; background: #f8fafc; min-height: 100vh; padding-bottom: 20px; }
        .dc-card { background: #fff; border-radius: 8px; border: 1px solid #e2e8f0; overflow: hidden; }
        .dc-header { background: var(--theme-color, #0088cc); padding: 10px 16px; color: white; border-bottom: 2px solid rgba(0,0,0,0.1); }
        .dc-header-brand { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 14px; }
        .dc-header-icon { width: 28px; height: 28px; background: rgba(255,255,255,0.2); border-radius: 6px; display: flex; align-items: center; justify-content: center; }
        .dc-body { padding: 16px; }
        .dc-label { font-size: 11px; font-weight: 600; color: #475569; margin-bottom: 4px; display: block; }
        .form-control-custom { width: 100%; height: 32px; padding: 4px 10px; font-size: 12px; border: 1px solid #cbd5e1; border-radius: 5px; outline: none; transition: 0.2s; }
        .form-control-custom:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,136,204,0.1); }
        .custom-switch .form-check-input { width: 2.5em; height: 1.2em; cursor: pointer; }
        .custom-switch .form-check-input:checked { background-color: #10b981; border-color: #10b981; }
        .custom-switch .form-check-input:focus { box-shadow: none; border-color: #cbd5e1; }
        .btn-submit { background: var(--theme-color, #0088cc); color: white; border: none; padding: 0 16px; height: 32px; border-radius: 5px; font-size: 12px; font-weight: 600; cursor: pointer; transition: 0.2s; }
        .btn-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
        .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; padding: 0 16px; height: 32px; border-radius: 5px; font-size: 12px; font-weight: 600; cursor: pointer; transition: 0.2s; }
        .btn-reset:hover:not(:disabled) { background: #e2e8f0; }
        .alert-custom { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 5px; font-size: 12px; margin-bottom: 15px; font-weight: 500; }
        .alert-danger { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
        .alert-success { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .slide-in { animation: slideIn 0.3s ease; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
        .section-header { background: #f0f4f8; padding: 8px 16px; border-bottom: 2px solid var(--theme-color, #0088cc); border-radius: 5px 5px 0 0; color: #1e293b; font-size: 13px; font-weight: 600; }
        .view-toggle { display: flex; background: #e2e8f0; border-radius: 5px; overflow: hidden; border: 1px solid #cbd5e1; }
        .view-toggle span { padding: 4px 12px; font-size: 11px; font-weight: 600; color: #475569; cursor: pointer; transition: 0.2s; display: flex; gap: 5px; align-items: center; }
        .view-toggle span.active { background: white; color: var(--theme-color, #0088cc); }
        .view-toggle span:hover:not(.active) { background: #f8fafc; }
        .search-input-wrapper { position: relative; display: flex; align-items: center; }
        .search-icon { position: absolute; left: 8px; color: #94a3b8; font-size: 12px; }
        .search-input { padding-left: 26px !important; }
        .dc-table-wrapper { overflow-x: auto; }
        .dc-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .dc-table th { background: #f8fafc; padding: 8px 12px; text-align: left; font-weight: 600; color: #475569; border-bottom: 1px solid #e2e8f0; }
        .dc-table td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; }
        .dc-table tbody tr:hover { background: #f8fafc; }
        .row-editing { background-color: #fffbeb !important; border-left: 3px solid #f59e0b; }
        .cards-container { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 15px; }
        .theme-glass-card { background: white; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.02); transition: 0.2s; display: flex; flex-direction: column; }
        .theme-glass-card:hover { transform: translateY(-2px); box-shadow: 0 6px 12px rgba(0,0,0,0.05); border-color: #cbd5e1; }
        .card-editing { border-color: #f59e0b; box-shadow: 0 0 0 2px rgba(245,158,11,0.2); }
        .glass-header { background: #f8fafc; padding: 10px 12px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f1f5f9; }
        .glass-title { font-weight: 700; font-size: 13px; color: #1e293b; max-width: 70%; }
        .glass-body { padding: 12px; flex: 1; }
        .glass-footer-btns { display: flex; border-top: 1px dashed #e2e8f0; padding-top: 12px; }
        .g-btn { padding: 6px; border-radius: 5px; border: 1px solid #e2e8f0; background: white; font-size: 11px; font-weight: 600; cursor: pointer; transition: 0.2s; }
        .g-btn-edit { color: #0284c7; } .g-btn-edit:hover { background: #f0f9ff; border-color: #bae6fd; }
        .g-btn-del { color: #dc2626; } .g-btn-del:hover { background: #fef2f2; border-color: #fecaca; }
        .action-buttons { display: flex; gap: 4px; justify-content: center; }
        .btn-icon { width: 26px; height: 26px; border-radius: 4px; border: none; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; transition: 0.2s; }
        .btn-edit { background: #e0f2fe; color: #0284c7; } .btn-edit:hover { background: #bae6fd; }
        .btn-delete { background: #fee2e2; color: #dc2626; } .btn-delete:hover { background: #fecaca; }
        .badge-status { padding: 4px 8px; border-radius: 12px; font-size: 10px; font-weight: 700; display: inline-flex; align-items: center; }
        .badge-status-sm { padding: 2px 6px; border-radius: 10px; font-size: 9px; font-weight: 700; display: inline-flex; align-items: center; }
        .badge-active { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .badge-inactive { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
        .badge-medicine { background: #dbeafe; color: #1d4ed8; border: 1px solid #bfdbfe; }
        .badge-medicine-no { background: #fef9c3; color: #854d0e; border: 1px solid #fef08a; }
        @media (max-width: 768px) {
          .section-header { flex-direction: column; align-items: stretch !important; gap: 10px; }
          .search-input-wrapper { width: 100% !important; }
        }
      `}</style>
    </div>
  );
};

export default PackageMaster;